<script setup lang="ts">
import { SentryService } from '@core/monitoring/sentry'
import { ref, onErrorCaptured, type ComponentPublicInstance } from 'vue'
import { logger } from '@core/logging/logger'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

const error = ref<Error | null>(null)

onErrorCaptured((err: Error, instance: ComponentPublicInstance | null, info: string) => {
  error.value = err
  logger.error('Captured by ErrorBoundary', err, { info })
  SentryService.captureException(err, {
    component: instance?.$options.name || 'Unknown',
    info,
  })
  return false
})

const resetError = () => {
  error.value = null
}
</script>

<template>
  <slot v-if="!error" />

  <Box v-else direction="col" gap="3" padding="4" class="error-boundary">
    <Box flex align="center" gap="2">
      <PiIcon name="exclamation-triangle" class="error-boundary__icon" />
      <Text type="body" weight="bold" color="danger">Something went wrong</Text>
    </Box>
    <Text type="body-sm" color="muted">{{ error.message }}</Text>
    <Button label="Try Again" variant="outline" size="small" @click="resetError" />
  </Box>
</template>

<style scoped>
.error-boundary {
  border: 1px solid var(--p-danger-300, #fda4a4);
  border-radius: 12px;
  background: var(--p-surface-0);
}

.error-boundary__icon {
  color: var(--p-danger-600);
  font-size: 1rem;
}
</style>
